import React, { useMemo } from 'react';
import { ExternalLink, QrCode, Smartphone, X } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const normalizeEnvUrl = (value) => String(value || '')
	.trim()
	.replace(/;$/, '')
	.replace(/^['"]|['"]$/g, '')
	.trim();

const getAndroidDeliveryAppUrl = () => normalizeEnvUrl(
	process.env.REACT_APP_ANDROID_DELIVERY_APP_URL ||
	process.env.REACT_APP_DELIVERY_APP_ANDROID_URL
);

const getIosDeliveryAppUrl = () => normalizeEnvUrl(
	process.env.REACT_APP_IOS_DELIVERY_APP_URL ||
	process.env.REACT_APP_DELIVERY_APP_IOS_URL
);

// The QR codes are drawn in the browser, so the install links never go to
// an outside image host and the popup works on a locked-down network.
const DeliveryAppDownloadModal = ({ isOpen, onClose }) => {
	const androidDownloadUrl = useMemo(() => getAndroidDeliveryAppUrl(), []);
	const iosDownloadUrl = useMemo(() => getIosDeliveryAppUrl(), []);

	const qrCards = [
		{
			label: 'Android',
			subtitle: 'Google Play',
			url: androidDownloadUrl,
			badgeClassName: 'bg-emerald-50 text-emerald-700 ring-emerald-100'
		},
		{
			label: 'iOS',
			subtitle: 'App Store',
			url: iosDownloadUrl,
			badgeClassName: 'bg-sky-50 text-sky-700 ring-sky-100'
		}
	];

	if (!isOpen) {
		return null;
	}

	return (
		<div
			style={{ zIndex: '9999999' }}
			className="fixed inset-0 flex items-center justify-center bg-slate-950/45 px-3 py-4 backdrop-blur-sm"
			role="dialog"
			aria-modal="true"
			aria-labelledby="delivery-app-download-title"
			onMouseDown={onClose}
		>
			<div
				className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.2)]"
				onMouseDown={(event) => event.stopPropagation()}
			>
				<div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-[#F3F6FF]" />

				<button
					type="button"
					className="absolute right-4 top-4 z-10 inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition hover:border-slate-300 hover:text-slate-800"
					onClick={onClose}
					aria-label="Close delivery app download popup"
				>
					<X className="h-4 w-4" />
				</button>

				<div className="relative px-5 pb-5 pt-5">
					<div className="flex items-start gap-3 pr-10">
						<span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-[#4E64C2]/10 text-[#4E64C2] ring-1 ring-[#4E64C2]/15">
							<Smartphone className="h-5 w-5" />
						</span>
						<div className="min-w-0">
							<h2 id="delivery-app-download-title" className="rubik-500 text-lg leading-tight text-slate-900">
								Delivery App Download
							</h2>
							<p className="rubik-400 mt-1 text-sm leading-snug text-slate-600">
								Scan the code for your device to install Medidoze Delivery.
							</p>
						</div>
					</div>

					<div className="mt-5 grid gap-4 sm:grid-cols-2">
						{qrCards.map((card) => (
							<div
								key={card.label}
								className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-center shadow-sm"
							>
								<div className="mb-3 flex items-center justify-center gap-2">
									<span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ring-1 ${card.badgeClassName}`}>
										{card.label}
									</span>
									<span className="text-xs font-medium text-slate-500">{card.subtitle}</span>
								</div>

								<div className="mx-auto w-fit rounded-2xl border border-slate-200 bg-white p-3 shadow-[0_8px_28px_rgba(78,100,194,0.1)]">
									{card.url ? (
										<QRCodeSVG
											value={card.url}
											size={176}
											marginSize={2}
											level="M"
											title={`QR code for ${card.label} Delivery App download`}
											className="rounded-lg"
										/>
									) : (
										<div className="flex h-[176px] w-[176px] items-center justify-center rounded-lg bg-slate-50 px-4 text-center text-xs font-medium text-slate-500">
											The {card.label} install link is not set up yet.
										</div>
									)}
								</div>

								<a
									href={card.url}
									target="_blank"
									rel="noreferrer"
									className="mt-3 inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-[#4E64C2] hover:text-[#3348A7]"
								>
									<QrCode className="h-3.5 w-3.5" />
									Open install link
									<ExternalLink className="h-3 w-3" />
								</a>
							</div>
						))}
					</div>

					<div className="mt-4 rounded-lg bg-[#F3F6FF] px-3 py-2 text-center">
						<p className="rubik-400 text-xs leading-snug text-slate-600">
							Scan the QR code to install the app and sign in with your delivery account.
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DeliveryAppDownloadModal;
